import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Switch } from '../ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { BankAccount, BankAccountCreate, BankAccountUpdate } from '../../types/settings'

interface BankAccountFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: BankAccountCreate | BankAccountUpdate) => Promise<void>
  account?: BankAccount | null
  isLoading?: boolean
}

export function BankAccountForm({
  open,
  onClose,
  onSubmit,
  account,
  isLoading = false,
}: BankAccountFormProps) {
  const { t } = useTranslation()
  const isEditing = !!account

  const [formData, setFormData] = useState<BankAccountCreate>({
    name: '',
    bankName: '',
    accountNumber: '',
    swiftBic: '',
    iban: '',
    isDefault: false,
  })

  useEffect(() => {
    if (account) {
      setFormData({
        name: account.name,
        bankName: account.bankName,
        accountNumber: account.accountNumber,
        swiftBic: account.swiftBic || '',
        iban: account.iban || '',
        isDefault: account.isDefault,
      })
    } else {
      setFormData({
        name: '',
        bankName: '',
        accountNumber: '',
        swiftBic: '',
        iban: '',
        isDefault: false,
      })
    }
  }, [account, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? t('settings.bankAccounts.edit')
                : t('settings.bankAccounts.add')}
            </DialogTitle>
            <DialogDescription>
              {t('settings.bankAccounts.formDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('settings.bankAccounts.name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('settings.bankAccounts.namePlaceholder')}
                required
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="bankName">{t('settings.bankAccounts.bankName')}</Label>
              <Input
                id="bankName"
                value={formData.bankName}
                onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                placeholder={t('settings.bankAccounts.bankNamePlaceholder')}
                required
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="iban">{t('settings.bankAccounts.iban')}</Label>
              <Input
                id="iban"
                value={formData.iban}
                onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                placeholder={t('settings.bankAccounts.ibanPlaceholder')}
                disabled={isLoading}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="accountNumber">
                  {t('settings.bankAccounts.accountNumber')}
                </Label>
                <Input
                  id="accountNumber"
                  value={formData.accountNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, accountNumber: e.target.value })
                  }
                  placeholder={t('settings.bankAccounts.accountNumberPlaceholder')}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="swiftBic">{t('settings.bankAccounts.swiftBic')}</Label>
                <Input
                  id="swiftBic"
                  value={formData.swiftBic}
                  onChange={(e) =>
                    setFormData({ ...formData, swiftBic: e.target.value })
                  }
                  placeholder={t('settings.bankAccounts.swiftBicPlaceholder')}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isDefault"
                checked={formData.isDefault}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isDefault: checked })
                }
                disabled={isLoading}
              />
              <Label htmlFor="isDefault">{t('settings.bankAccounts.setAsDefault')}</Label>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving')}
                </>
              ) : (
                t('common.save')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
